import { BLCommonFields as DefaultBLCommonFields, MBL_MAPPING_TO_HBL } from '@/common/constants';
import { HBL, Shipment, ShipmentType, Todo, VolumeUnits } from '@/common/type';
import { FormInstance } from 'antd';
import { floor, forEach, get, map, pick, round } from 'lodash';
import { cmFactor, convertUnit } from '@/common/utils';
import { getShopSetting } from '@/utils';

const HBL_FILING_NUMBER_FIELDS = ['ams_no', 'isf_no'];

export const SHIPMENT_NO_LABELS_FILTER_VALUE = '__shipment_no_labels__';
const SHIPMENT_LABEL_FILTER_VALUE_PREFIX = '__shipment_label__:';

export const encodeShipmentLabelFilterValue = (labelName: string) =>
  `${SHIPMENT_LABEL_FILTER_VALUE_PREFIX}${labelName}`;

export const buildShipmentLabelValueEnum = (
  labels: Array<{ name: string }> = [],
  noLabelsText: string,
): Record<string, string> =>
  labels.reduce(
    (options, label) => {
      options[encodeShipmentLabelFilterValue(label.name)] = label.name;
      return options;
    },
    { [SHIPMENT_NO_LABELS_FILTER_VALUE]: noLabelsText } as Record<string, string>,
  );

export const buildShipmentLabelSearchParams = (
  value?: string,
  relationPath = 'labels',
): Record<string, string | boolean> => {
  if (!value) {
    return {};
  }
  if (value === SHIPMENT_NO_LABELS_FILTER_VALUE) {
    return { [`${relationPath}.id_null`]: true };
  }
  return {
    [`${relationPath}.name`]: value.startsWith(SHIPMENT_LABEL_FILTER_VALUE_PREFIX)
      ? value.slice(SHIPMENT_LABEL_FILTER_VALUE_PREFIX.length)
      : value,
  };
};

export const buildShipmentLabelFilterValueFromQuery = (
  query: Record<string, any> = {},
  relationPath = 'labels',
): string | undefined => {
  if (query[`${relationPath}.id_null`] === true || query[`${relationPath}.id_null`] === 'true') {
    return SHIPMENT_NO_LABELS_FILTER_VALUE;
  }
  const labelName = query[`${relationPath}.name`];
  return typeof labelName === 'string' && labelName
    ? encodeShipmentLabelFilterValue(labelName)
    : undefined;
};

export const getSyncableBLCommonFields = (overrides?: string[]) =>
  (overrides || DefaultBLCommonFields).filter(field => !HBL_FILING_NUMBER_FIELDS.includes(field));

// Number rounding configuration interfaces
interface RoundingConfig {
  method: 'floor' | 'ceil' | 'round';
  precision: number;
}

interface NumberRoundingSettings {
  fields: Record<string, RoundingConfig>;
  default: RoundingConfig;
}

export interface ShipmentContactAutoPopulateSettings {
  customer: {
    consignee: boolean;
    sales: boolean;
    operator: boolean;
  };
  consignee: {
    customer: boolean;
    bill_to: boolean;
    notify: boolean;
  };
  agent: {
    sales: boolean;
    operator: boolean;
  };
}

const DEFAULT_SHIPMENT_CONTACT_AUTO_POPULATE_SETTINGS: ShipmentContactAutoPopulateSettings = {
  customer: {
    consignee: true,
    sales: true,
    operator: true,
  },
  consignee: {
    customer: true,
    bill_to: true,
    notify: true,
  },
  agent: {
    sales: true,
    operator: true,
  },
};

type ShipmentContactAutoPopulateSettingsInput = {
  customer?: {
    consignee?: boolean;
    sales?: boolean;
    operator?: boolean;
  };
  consignee?: {
    customer?: boolean;
    bill_to?: boolean;
    notify?: boolean;
  };
  mbl?: ShipmentContactAutoPopulateSettingsInput;
  hbl?: ShipmentContactAutoPopulateSettingsInput;
} & Record<string, any>;

// Utility functions for number rounding
const getNumberRoundingSettings = (userData: any): NumberRoundingSettings => {
  const settings = getShopSetting(userData, 'number_rounding_settings', 'message') || {};

  return {
    fields: settings.fields || {},
    default: settings.default || { method: 'floor', precision: 3 },
  };
};

export const getShipmentContactAutoPopulateSettings = (
  userData: any,
  shipmentType?: ShipmentType,
  level?: 'mbl' | 'hbl',
  office?: string,
): ShipmentContactAutoPopulateSettings => {
  const setting = getShopSetting(userData, 'shipment_contact_auto_populate', 'message', office);
  const rawSetting =
    typeof setting === 'string'
      ? (() => {
          try {
            return JSON.parse(setting);
          } catch {
            return undefined;
          }
        })()
      : setting;

  if (!rawSetting || typeof rawSetting !== 'object') {
    return DEFAULT_SHIPMENT_CONTACT_AUTO_POPULATE_SETTINGS;
  }

  const mergeRuleLayer = (
    base: ShipmentContactAutoPopulateSettings,
    layer?: ShipmentContactAutoPopulateSettingsInput,
  ): ShipmentContactAutoPopulateSettings => {
    if (!layer || typeof layer !== 'object') {
      return base;
    }

    return {
      customer: {
        consignee:
          typeof layer?.customer?.consignee === 'boolean'
            ? layer.customer.consignee
            : base.customer.consignee,
        sales:
          typeof layer?.customer?.sales === 'boolean' ? layer.customer.sales : base.customer.sales,
        operator:
          typeof layer?.customer?.operator === 'boolean'
            ? layer.customer.operator
            : base.customer.operator,
      },
      consignee: {
        customer:
          typeof layer?.consignee?.customer === 'boolean'
            ? layer.consignee.customer
            : base.consignee.customer,
        bill_to:
          typeof layer?.consignee?.bill_to === 'boolean'
            ? layer.consignee.bill_to
            : base.consignee.bill_to,
        notify:
          typeof layer?.consignee?.notify === 'boolean'
            ? layer.consignee.notify
            : base.consignee.notify,
      },
      agent: {
        sales: typeof layer?.agent?.sales === 'boolean' ? layer.agent.sales : base.agent.sales,
        operator:
          typeof layer?.agent?.operator === 'boolean' ? layer.agent.operator : base.agent.operator,
      },
    };
  };

  const parsedSetting = rawSetting as ShipmentContactAutoPopulateSettingsInput;
  const typeLayer =
    shipmentType && parsedSetting?.[shipmentType] && typeof parsedSetting[shipmentType] === 'object'
      ? (parsedSetting[shipmentType] as ShipmentContactAutoPopulateSettingsInput)
      : undefined;
  const levelLayer =
    level && typeLayer?.[level] && typeof typeLayer[level] === 'object'
      ? (typeLayer[level] as ShipmentContactAutoPopulateSettingsInput)
      : undefined;

  return [parsedSetting, typeLayer, levelLayer].reduce(
    (settings, layer) => mergeRuleLayer(settings, layer),
    DEFAULT_SHIPMENT_CONTACT_AUTO_POPULATE_SETTINGS,
  );
};

export const roundNumber = (value: number, fieldName: string, userData?: any): number => {
  if (!userData) {
    // If no userData is passed, use default behavior (floor with 3 precision)
    return floor(value, 3);
  }

  const settings = getNumberRoundingSettings(userData);
  const config = settings.fields[fieldName] || settings.default;

  const multiplier = Math.pow(10, config.precision);

  switch (config.method) {
    case 'ceil':
      return Math.ceil(value * multiplier) / multiplier;
    case 'round':
      return Math.round(value * multiplier) / multiplier;
    case 'floor':
    default:
      return Math.floor(value * multiplier) / multiplier;
  }
};

export const ContactFields = [
  'carrier',
  'transit_carrier',
  'agent',
  'coloader',
  'cy_location',
  'cfs_location',
  'container_return_location',
  'shipper',
  'consignee',
  'notify',
  'additional_notify',
  'customer',
  'bill_to',
  'trucker',
  'cycfs_location',
  'delivery_location',
];

export function getRoutePath({
  ext_id,
  type,
  hbl,
  tab,
}: {
  ext_id?: string;
  type: ShipmentType;
  hbl?: string;
  tab?: string;
}) {
  return `/shipments/${type}/${ext_id || 'new'}${
    tab && ['accounting'].includes(tab) ? `/${tab}` : ''
  }${hbl ? `?hbl=${hbl}` : ''}`;
}

// The post-date rungs of the ladder below -- the fields that only exist for WH / TR / MIS.
export const SHIPMENT_POST_DATE_FIELDS = ['post_at', 'shipment_date'];

// Ordered list of fields to read the accounting base date from, per shipment type. Mirrors
// shipment.getBaseDateFields in the api: WH / TR / MIS anchor on their own post date and only fall
// back to ETA/ETD when it is empty.
export function getBaseDateFields(type?: ShipmentType | string, featureMiscUseETD?: boolean) {
  if (type?.endsWith('import')) {
    return ['eta'];
  }
  if (type?.endsWith('export')) {
    return ['etd'];
  }
  const fallback = featureMiscUseETD ? 'etd' : 'eta';
  if (['warehouse_receiving', 'warehouse_shipping'].includes(type || '')) {
    return ['shipment_date', fallback];
  }
  if (['misc', 'truck'].includes(type || '')) {
    return ['post_at', fallback];
  }
  return [fallback];
}

// Fields hidden for this shipment type, most specific setting wins. Note the form only marks these
// `hidden`, so the value is still submitted — check this before seeding a default into one.
export function getExcludedShipmentFields(userData: any, type?: ShipmentType | string): string[] {
  return (
    getShopSetting(userData, `excluded_field_mbl_${type}`, 'message') ||
    getShopSetting(userData, `excluded_field_${type}`, 'message') ||
    getShopSetting(userData, 'excluded_field_shipment', 'message') ||
    []
  );
}

// First non-empty base date on a shipment. post_at / shipment_date are mirrored onto the shipment
// by the api; fall back to the ombl so this stays correct for shipments saved before the mirror.
export function getShipmentBaseDate(
  shipment: Partial<Shipment> | undefined,
  featureMiscUseETD?: boolean,
): string | undefined {
  if (!shipment) {
    return undefined;
  }
  for (const field of getBaseDateFields(shipment.type, featureMiscUseETD)) {
    const value = get(shipment, field) || get(shipment, `ombl.${field}`);
    if (value) {
      return value as string;
    }
  }
  return undefined;
}

/**
 * The shipment's own post date -- `post_at` for truck / misc, `shipment_date` for warehouse --
 * and nothing for ocean and air, which have none. Unlike getShipmentEtdEtaDate this does NOT
 * fall back to ETA/ETD: it backs a dedicated Post Date column, where an empty cell is the honest
 * answer for a shipment that has no post date.
 */
export function getShipmentPostDate(shipment: Partial<Shipment> | undefined): string | undefined {
  if (!shipment) {
    return undefined;
  }
  for (const field of getBaseDateFields(shipment.type).filter(f =>
    SHIPMENT_POST_DATE_FIELDS.includes(f),
  )) {
    const value = get(shipment, field) || get(shipment, `ombl.${field}`);
    if (value) {
      return value as string;
    }
  }
  return undefined;
}

/**
 * The date an ETD/ETA column should show, matching what the api sorts and filters that column by
 * (shipment-date-sql.js). The flag picks which single rung a WH / TR / MIS shipment keeps: with it
 * off the post-date rung is dropped and the column falls back to ETA/ETD, with it on the ETA/ETD
 * rung is dropped and the post date stands alone -- so a warehouse shipment with no shipment_date
 * shows nothing, which is the honest answer and exactly what the api now filters by. Keep the two
 * in step: a column that displays one date and sorts by another is the bug this replaced.
 */
export function getShipmentEtdEtaDate(
  shipment: Partial<Shipment> | undefined,
  featureMiscUseETD?: boolean,
  featureReportUseShipmentPostDate?: boolean,
): string | undefined {
  if (!shipment) {
    return undefined;
  }
  const base = getBaseDateFields(shipment.type, featureMiscUseETD);
  let fields = base.filter(field => !SHIPMENT_POST_DATE_FIELDS.includes(field));
  if (featureReportUseShipmentPostDate) {
    const postRungs = base.filter(field => SHIPMENT_POST_DATE_FIELDS.includes(field));
    fields = postRungs.length ? postRungs : fields;
  }
  for (const field of fields) {
    const value = get(shipment, field) || get(shipment, `ombl.${field}`);
    if (value) {
      return value as string;
    }
  }
  return undefined;
}

export function getValuesByKey(record: Shipment, key: string, subKey?: string) {
  const values = [];
  if (get(record, `ombl.${key}${subKey ? `.${subKey}` : ''}`)) {
    values.push(get(record, `ombl.${key}${subKey ? `.${subKey}` : ''}`));
  }
  for (const hbl of record.hbls || ([] as HBL[])) {
    const value = get(hbl, `${key}${subKey ? `.${subKey}` : ''}`);
    if (value && !values.includes(value)) {
      values.push(value);
    }
  }
  return values;
}

export function renderCommaSeparated(record: Shipment, key: string, subKey?: string) {
  const values = getValuesByKey(record, key, subKey);
  return values.length ? values.join(', ') : '-';
}

/**
 * The note shown under a todo. Generated todos carry it in the `description` column, copied
 * from the matching `todo_instructions` rule; manually created ones still use
 * `additional_info.description`, so fall back to that.
 */
export function getTodoDescription(todo: Todo): string | undefined {
  return todo.description || get(todo, 'additional_info.description') || undefined;
}

export function findHBLByIDOrExtID(hbls: HBL[], id: string) {
  return id.indexOf('-') > 0
    ? hbls.find(hbl => hbl.ext_id === id)
    : hbls.find(hbl => `${hbl.id}` === id);
}

export function getInitHblValuesFromMblForm(
  form: FormInstance,
  BLCommonFields = DefaultBLCommonFields,
  MBLMappingToHBL = MBL_MAPPING_TO_HBL,
) {
  const mblValues = pick(
    form.getFieldsValue(),
    BLCommonFields.concat(Object.keys(MBLMappingToHBL)),
  );
  forEach(mblValues, (value, key) => {
    const newKey = MBLMappingToHBL?.[key as keyof typeof MBLMappingToHBL];
    if (newKey) {
      if (!!mblValues[newKey]) {
        return;
      }
      mblValues[newKey] = value;
      key = newKey;
    }
    if (ContactFields.includes(key) && value) {
      mblValues[key] = { id: `${value}` };
    } else if (['operator', 'sales'].includes(key) && value) {
      mblValues[key] = { id: value };
    } else if (key === 'co_operators' && value?.length) {
      mblValues[key] = value.map(co_operator => ({ id: `${co_operator}` }));
    }
  });
  const containers = map(
    form.getFieldValue('containers'),
    ({ id, mbl, ext_id, updated_at, ...c }) => ({
      ...c,
      mbl_container: id,
      ...(!id ? { mbl_container_ext_id: ext_id } : {}),
      ext_id: `new_${Math.floor(Math.random() * 1000000)}`,
    }),
  );
  const fieldList = [
    'volumeUnit',
    'weightUnit',
    'package',
    'commodity_info',
    'mark',
    'soc_address',
    'arrival_notice_remark',
    'delivery_order_remark',
    'handling_information',
    'pieces',
    'weight',
    'measure',
  ];
  const otherFields = form.getFieldsValue(fieldList) || {};

  return {
    ...mblValues,
    containers,
    ...otherFields,
  };
}

export interface WeightInfo {
  gross_weight?: number;
  gross_weight_unit?: string;
  gross_weight_lb?: number;
  chargeable_weight?: number;
  chargeable_weight_unit?: string;
  chargeable_weight_lb?: number;
  volume_weight?: number;
  volume_weight_unit?: string;
  volume_measure?: number;

  consignee_gross_weight?: number;
  consignee_gross_weight_unit?: string;
  consignee_gross_weight_lb?: number;
  consignee_chargeable_weight?: number;
  consignee_chargeable_weight_unit?: string;
  consignee_chargeable_weight_lb?: number;

  shipper_gross_weight?: number;
  shipper_gross_weight_unit?: string;
  shipper_gross_weight_lb?: number;
  shipper_chargeable_weight?: number;
  shipper_chargeable_weight_unit?: string;
  shipper_chargeable_weight_lb?: number;
}

export interface WeightValues {
  weight?: number;
  weightUnit?: string;
  measure?: number;
  volumeUnit?: string;
  weight_info?: WeightInfo;
  chargeable_weight?: number;
  chargeable_weight_unit?: string;
}

export const measureFromLWH = (values: {
  size_length: number;
  size_width: number;
  size_height: number;
  size_unit?: string;
  volumeUnit?: string;
}) => {
  let measure = values.size_length * values.size_width * values.size_height;
  const sizeUnit = values.size_unit || 'M';
  if (sizeUnit !== 'M') {
    measure = measure * cmFactor(sizeUnit) * 0.000001;
  }
  if (values.volumeUnit === VolumeUnits.CFT) {
    measure = convertUnit(measure, 'CBM', 'CFT');
  }
  return round(measure, 9);
};

export const updateWeightInfo = (values: WeightValues, userData?: any): Partial<WeightValues> => {
  const weightKg = values.weight
    ? values.weightUnit === 'KG'
      ? values.weight
      : convertUnit(values.weight, 'LB', 'KG')
    : 0;

  const measure = values.measure
    ? values.volumeUnit === 'CBM'
      ? values.measure
      : convertUnit(values.measure, 'CFT', 'CBM')
    : 0;

  const volumeWeight = roundNumber(convertUnit(measure, 'CBM', 'KG'), 'volume_weight', userData);
  const chargeableWeight = roundNumber(
    Math.max(weightKg, volumeWeight),
    'chargeable_weight',
    userData,
  );

  return {
    chargeable_weight: chargeableWeight,
    chargeable_weight_unit: 'KG',
    weight_info: {
      gross_weight: weightKg,
      gross_weight_unit: 'KG',
      gross_weight_lb: roundNumber(convertUnit(weightKg, 'KG', 'LB'), 'gross_weight_lb', userData),
      chargeable_weight: chargeableWeight,
      chargeable_weight_unit: 'KG',
      chargeable_weight_lb: roundNumber(
        convertUnit(chargeableWeight, 'KG', 'LB'),
        'chargeable_weight_lb',
        userData,
      ),
      volume_weight: volumeWeight,
      volume_weight_unit: 'KG',
      volume_measure: measure,

      consignee_gross_weight: weightKg,
      consignee_gross_weight_unit: 'KG',
      consignee_gross_weight_lb: roundNumber(
        convertUnit(weightKg, 'KG', 'LB'),
        'consignee_gross_weight_lb',
        userData,
      ),
      consignee_chargeable_weight: chargeableWeight,
      consignee_chargeable_weight_unit: 'KG',
      consignee_chargeable_weight_lb: roundNumber(
        convertUnit(chargeableWeight, 'KG', 'LB'),
        'consignee_chargeable_weight_lb',
        userData,
      ),

      shipper_gross_weight: weightKg,
      shipper_gross_weight_unit: 'KG',
      shipper_gross_weight_lb: roundNumber(
        convertUnit(weightKg, 'KG', 'LB'),
        'shipper_gross_weight_lb',
        userData,
      ),
      shipper_chargeable_weight: chargeableWeight,
      shipper_chargeable_weight_unit: 'KG',
      shipper_chargeable_weight_lb: roundNumber(
        convertUnit(chargeableWeight, 'KG', 'LB'),
        'shipper_chargeable_weight_lb',
        userData,
      ),
    },
  };
};

export const updateVolumeWeightInfo = (
  volumeWeight: number,
  currentWeightInfo?: WeightInfo,
  userData?: any,
): Partial<WeightValues> => {
  const measure = convertUnit(volumeWeight, 'KG', 'CBM');

  return {
    measure,
    volumeUnit: 'CBM',
    weight_info: {
      ...currentWeightInfo,
      volume_weight: volumeWeight,
      volume_weight_unit: 'KG',
      volume_measure: measure,

      consignee_chargeable_weight: volumeWeight,
      consignee_chargeable_weight_unit: 'KG',
      consignee_chargeable_weight_lb: roundNumber(
        convertUnit(volumeWeight, 'KG', 'LB'),
        'consignee_chargeable_weight_lb',
        userData,
      ),

      shipper_chargeable_weight: volumeWeight,
      shipper_chargeable_weight_unit: 'KG',
      shipper_chargeable_weight_lb: roundNumber(
        convertUnit(volumeWeight, 'KG', 'LB'),
        'shipper_chargeable_weight_lb',
        userData,
      ),
    },
  };
};

export const updateChargeableWeightInfo = (
  chargeableWeight: number,
  currentWeightInfo?: WeightInfo,
  userData?: any,
): Partial<WeightValues> => {
  return {
    chargeable_weight: chargeableWeight,
    chargeable_weight_unit: 'KG',
    weight_info: {
      ...currentWeightInfo,
      chargeable_weight: chargeableWeight,
      chargeable_weight_unit: 'KG',
      chargeable_weight_lb: roundNumber(
        convertUnit(chargeableWeight, 'KG', 'LB'),
        'chargeable_weight_lb',
        userData,
      ),

      consignee_chargeable_weight: chargeableWeight,
      consignee_chargeable_weight_unit: 'KG',
      consignee_chargeable_weight_lb: roundNumber(
        convertUnit(chargeableWeight, 'KG', 'LB'),
        'consignee_chargeable_weight_lb',
        userData,
      ),

      shipper_chargeable_weight: chargeableWeight,
      shipper_chargeable_weight_unit: 'KG',
      shipper_chargeable_weight_lb: roundNumber(
        convertUnit(chargeableWeight, 'KG', 'LB'),
        'shipper_chargeable_weight_lb',
        userData,
      ),
    },
  };
};

export const updateGrossWeightInfo = (
  grossWeight: number,
  currentWeightInfo?: WeightInfo,
  userData?: any,
): Partial<WeightValues> => {
  return {
    weight: grossWeight,
    weightUnit: 'KG',
    weight_info: {
      ...currentWeightInfo,
      gross_weight: grossWeight,
      gross_weight_unit: 'KG',
      gross_weight_lb: roundNumber(
        convertUnit(grossWeight, 'KG', 'LB'),
        'gross_weight_lb',
        userData,
      ),

      consignee_gross_weight: grossWeight,
      consignee_gross_weight_unit: 'KG',
      consignee_gross_weight_lb: roundNumber(
        convertUnit(grossWeight, 'KG', 'LB'),
        'consignee_gross_weight_lb',
        userData,
      ),

      shipper_gross_weight: grossWeight,
      shipper_gross_weight_unit: 'KG',
      shipper_gross_weight_lb: roundNumber(
        convertUnit(grossWeight, 'KG', 'LB'),
        'shipper_gross_weight_lb',
        userData,
      ),
    },
  };
};
